import { NumberHelpers } from "helpers/number";

export type ColorFormat = 'hex' | 'rgb';

const regexps = {
    rgba: /rgba\((\d+),\s*(\d+),\s*(\d+),\s*([0-9.]+)\)/,
    rgb: /rgb\((\d+),\s*(\d+),\s*(\d+)\)/,
    shortHex: /^#[\dabcdef]{3,4}$/,
    hex: /^#[\dabcdef]{6,8}$/
}

function toFullHex(color: string) {
    let result = '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3];

    if (color.length == 5)
        result += color[4] + color[4];
    else
        result += 'ff';

    return result;
}

function validateColorChannelValue(value: number) {
    if (value < 0 || value > 255)
        throw new Error('invalid value');
}

function validateAlphaChannelValue(value: number) {
    if (value < 0 || value > 1)
        throw new Error('invalid value');
}

export default class Color {
    private _red = 0
    private _green = 0;
    private _blue = 0;
    private _alpha = 1;

    get red() {
        return this._red;
    }

    set red(value: number) {
        validateColorChannelValue(value);

        this._red = value;
    }

    get green() {
        return this._green;
    }

    set green(value: number) {
        validateColorChannelValue(value);

        this._green = value;
    }

    get blue() {
        return this._blue;
    }

    set blue(value: number) {
        validateColorChannelValue(value);

        this._blue = value;
    }    

    get alpha() {
        return this._alpha;
    }

    set alpha(value: number) {
        validateAlphaChannelValue(value);

        this._alpha = value;
    }

    static from(color: string) {
        return new Color(color);
    }

    constructor(color: string) {
        if (regexps.shortHex.test(color) || regexps.hex.test(color))
            this.initFromHex(color);

        if (regexps.rgb.test(color))
            this.initFromRGB(color);

        if (regexps.rgba.test(color))
            this.initFromRGBA(color);
    }    

    setRed(value: number) {
        this.red = value;
        return this;
    }

    setGreen(value: number) {
        this.green = value;
        return this;
    }

    setBlue(value: number) {
        this.blue = value;
        return this;
    }

    setAlpha(value: number) {
        this.alpha = value;
        return this;
    }

    format(type?: ColorFormat) {
        switch (type) {
            case 'rgb':
                if (this._alpha == 1)
                    return `rgb(${this._red.toString()}, ${this._green.toString()}, ${this._blue.toString()})`;
                else
                    return `rgba(${this._red.toString()}, ${this._green.toString()}, ${this._blue.toString()}, ${this._alpha.toString()})`;

            default:
                if (this._alpha == 1)
                    return `#${NumberHelpers.toHex(this._red)}${NumberHelpers.toHex(this._green)}${NumberHelpers.toHex(this._blue)}`;
                else
                    return `#${NumberHelpers.toHex(this._red)}${NumberHelpers.toHex(this._green)}${NumberHelpers.toHex(this._blue)}${NumberHelpers.toHex(Math.floor(this._alpha * 255))}`;
        }
    }

    compare(other: Color) {
        return this.red === other.red &&
            this.green === other.green &&
            this.blue === other.blue &&
            this.alpha === other.alpha;
    }

    private initFromHex(color: string) {
        if (regexps.shortHex.test(color))
            color = toFullHex(color) ?? color;

        this._red = parseInt(color.slice(1, 3), 16);
        this._green = parseInt(color.slice(3, 5), 16);
        this._blue = parseInt(color.slice(5, 7), 16);
        this._alpha = parseInt(color.slice(7, 9), 16) / 255;
    }    

    private initFromRGB(color: string) {
        const match = regexps.rgb.exec(color);

        if (match) {
            this._red = parseInt(match[1]);
            this._green = parseInt(match[2]);
            this._blue = parseInt(match[3]);
        }
    }

    private initFromRGBA(color: string) {
        const match = regexps.rgba.exec(color);

        if (match) {
            this._red = parseInt(match[1]);
            this._green = parseInt(match[2]);
            this._blue = parseInt(match[3]);
            this._alpha = parseInt(match[4]);
        }
    }
}